import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  AUTH_STORAGE_KEY,
  DEFAULT_AUTH_CREDENTIALS,
  DEFAULT_AUTH_USER,
} from './authConfig';

const AuthContext = createContext(undefined);

const readStoredSession = () => {
  try {
    const storedSession = localStorage.getItem(AUTH_STORAGE_KEY);

    if (!storedSession) {
      return null;
    }

    const parsedSession = JSON.parse(storedSession);

    if (!parsedSession?.isAuthenticated || !parsedSession?.user) {
      return null;
    }

    return parsedSession;
  } catch (error) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(readStoredSession);

  useEffect(() => {
    if (session?.isAuthenticated) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
      return;
    }

    localStorage.removeItem(AUTH_STORAGE_KEY);
  }, [session]);

  const login = useCallback(({ identifier, password }) => {
    const normalizedIdentifier = identifier.trim().toLowerCase();
    const allowedIdentifiers = [
      DEFAULT_AUTH_CREDENTIALS.username.toLowerCase(),
      DEFAULT_AUTH_CREDENTIALS.email.toLowerCase(),
    ];

    const isAllowedIdentifier = allowedIdentifiers.includes(normalizedIdentifier);
    const isAllowedPassword = password === DEFAULT_AUTH_CREDENTIALS.password;

    if (!isAllowedIdentifier || !isAllowedPassword) {
      return {
        ok: false,
        error: 'Invalid username/email or password.',
      };
    }

    const nextSession = {
      isAuthenticated: true,
      authenticatedAt: new Date().toISOString(),
      user: DEFAULT_AUTH_USER,
    };

    setSession(nextSession);

    return {
      ok: true,
      user: DEFAULT_AUTH_USER,
    };
  }, []);

  const logout = useCallback(() => {
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: Boolean(session?.isAuthenticated),
        user: session?.user ?? null,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
